import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';

import { CurrencyFormat } from '@ha/intl';
import { Button, Grid, Divider, Typography, units } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import { normalizeDate } from '@ha/date';
import { buildParams } from '@ha/search-query';
import { Link } from 'ha/ui.legacy/Link';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import * as Section from 'ha/ui.legacy/Section';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { usePricing, FeeMode, PricingStructure } from 'ha/modules/Pricing';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import {
  DATEPICKER_VARIANT,
  DateRangePicker,
  DateRangePickerInputsOutlined,
} from '@ha/marketplace/feature-date-picker';
import { constants } from 'ha/constants/commonConstants';
import { TextTooltip } from 'ha/components.legacy/TextTooltip';

import { PaymentOverviewButton } from '../../components/PaymentOverviewButton';
import { ListingContactAdvertiserButton } from '../ListingContactAdvertiserButton';
import { PromotionalBookingContainer as PromotionalBooking } from '../../containers/PromotionalBookingContainer';
import { AnchorLink } from '../../components/AnchorLink';
import { useLocalPricing } from '../../hooks/useLocalPricing';
import { useDesiredDates } from '../../hooks/useDesiredDates';
import { useDesiredDatesPicker } from '../../containers/DesiredDatesPicker';
import {
  isAdvertiserOwner,
  getMinimalRentalPeriod,
  getMaximumStay,
  getPrice,
  getHaFee,
  getBaseHaFee,
  getTotal,
  getListingId,
  getCurrency,
  getIsUnlisted,
  getPathToSearch,
  getListingErrorMessage,
  getRentalConditions,
  getTenantFeeRuleName,
  getListingIsCharitable,
  getListingIsFree,
} from '../../selectors';
import {
  ListingAdvancedPricingPill,
  ListingAdvancedPricingInfo,
} from '../ListingAdvancedPricing';

import { usePageRedesign } from '../../hooks/usePageRedesign';
import { PartnerListingInfo } from '../../components/PartnerListingInfo';

const useStyles = makeStyles()(theme => ({
  buttonRedesign: {
    boxShadow: 'none',
  },
  container: {
    padding: units.rem(1.5),
  },
  haFeeFreeAmount: {
    color: theme.palette.grey[600],
    textDecoration: 'line-through',
  },
  haFeeFreeLabel: {
    marginLeft: theme.spacing(0.5),
  },
  faqSection: {
    textAlign: 'center',
    marginTop: units.rem(0.5),
  },
  faqLink: {
    textDecoration: 'underline',
  },
  hr: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  spacer: {
    padding: theme.spacing(0.5),
  },
  divider: {
    margin: units.yx(1, 0),
  },
  paymentDialogButton: {
    textAlign: 'center',
    padding: theme.spacing(0.5),
  },
  paymentDialogButtonRedesign: {
    textAlign: 'left',
  },
}));

interface Props {
  isBelongsToPartner?: boolean;
}

// eslint-disable-next-line complexity
const ListingInstantBookingBlock: React.FC<Props> = ({
  isBelongsToPartner,
}) => {
  const { classes, cx } = useStyles();
  const { T, formatDate: formatDateIntl, locale, formatCurrency } = useIntl();
  const { sm } = useMediaQuery();
  const { promotionalBookingFee } = useFeatureFlags();
  const [searchParams] = useSearchParams();
  const queryStartDate = searchParams.get('startDate') ?? '';
  const withPageRedesign = usePageRedesign();

  const { pricingStructure } = useLocalPricing();

  const { hasDesiredDates } = useDesiredDates();

  const haFee = useSelector(getHaFee);
  const baseHaFee = useSelector(getBaseHaFee);
  const price = useSelector(getPrice);
  const total = useSelector(getTotal);
  const currency = useSelector(getCurrency);
  const isOwner = useSelector(isAdvertiserOwner);
  const minimalPeriod = useSelector(getMinimalRentalPeriod);
  const maximumStay = useSelector(getMaximumStay);
  const listingId = useSelector(getListingId);
  const isUnlisted = useSelector(getIsUnlisted);
  const searchPath = useSelector(getPathToSearch);
  const listingErrorMessage = useSelector(getListingErrorMessage);
  const tenantFeeRuleName = useSelector(getTenantFeeRuleName);
  const isCharitable = useSelector(getListingIsCharitable);
  const isFreeListing = useSelector(getListingIsFree);
  const { startDate, endDate } = useDesiredDates().desiredDates;

  const rentalConditions = useSelector(getRentalConditions) || {
    isLegacy: true,
    firstInstalment: 0,
    tenantFee: haFee,
    baseTenantFee: baseHaFee,
    currency,
    startDate: queryStartDate,
  };
  const { feeMode, fee, baseFee } = usePricing({
    listingId,
    rentalConditions:
      pricingStructure === PricingStructure.instalment && rentalConditions
        ? rentalConditions
        : {
            isLegacy: true,
            tenantFee: haFee,
            baseTenantFee: baseHaFee,
            currency,
            startDate: queryStartDate,
          },
    tenantFeeRuleName,
    pricingStructureOverride: pricingStructure,
  });

  const currencyFormat =
    currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

  const { currentValue, onDateChange, dateRangePickerProps } =
    useDesiredDatesPicker();

  const monthRentPopoverText = T(
    "The first month's rent is paid to Housing Anywhere and will be transferred to the advertiser 48 hours after you move in",
  );
  const serviceFeePopoverText = T(
    'This helps us run and improve the platform. It also allows us to offer you customer support and guarantees you will get the room you book.',
  );
  const translatedListingErrorMessage = T(listingErrorMessage, {
    minPeriod: minimalPeriod,
    maxPeriod: maximumStay,
  });

  if (isBelongsToPartner) return <PartnerListingInfo />;

  return (
    <Section.Text
      className={classes.container}
      data-test-locator="Contact Advertiser Block"
    >
      {!withPageRedesign && <div className={classes.spacer} />}

      {isUnlisted && (
        <React.Fragment>
          <Typography.Paragraph color="error" align="center">
            {T('This listing is not available anymore')}
          </Typography.Paragraph>

          <div className={classes.spacer} />
        </React.Fragment>
      )}

      {!!translatedListingErrorMessage && (
        <React.Fragment>
          <Typography.Paragraph color="error" align="center">
            {translatedListingErrorMessage}
          </Typography.Paragraph>

          <div className={classes.spacer} />
        </React.Fragment>
      )}

      {!isCharitable && (
        <React.Fragment>
          {!withPageRedesign && (
            <React.Fragment>
              <Typography.ParagraphTitle textTransform="uppercase">
                {T('Rental period')}
              </Typography.ParagraphTitle>

              <div className={classes.spacer} />
            </React.Fragment>
          )}

          <DateRangePicker
            CaptionSubtitle={ListingAdvancedPricingPill}
            header={<ListingAdvancedPricingInfo />}
            variant={DATEPICKER_VARIANT.RESPONSIVE}
            onDateChange={onDateChange}
            value={currentValue}
            showSingleMonth={!sm}
            T={T}
            formatDate={formatDateIntl}
            localeString={locale}
            listingFlexibleDays={constants.listingFlexibleDays}
            DatePickerInputs={
              withPageRedesign ? DateRangePickerInputsOutlined : undefined
            }
            {...dateRangePickerProps}
          />

          <div className={classes.spacer} />
        </React.Fragment>
      )}

      {!withPageRedesign &&
        pricingStructure === PricingStructure.instalment &&
        hasDesiredDates && (
          <div className={classes.paymentDialogButton}>
            <PaymentOverviewButton
              text={T('StructuredCosts.LDP.cta_fullpaymentoverview')}
              startDate={rentalConditions.startDate}
            />
          </div>
        )}

      {isUnlisted && (
        <React.Fragment>
          <Track name="Clicked Show alternatives" type="link">
            <Link
              to={`${searchPath}${buildParams({
                startDate: normalizeDate(startDate),
                endDate: normalizeDate(endDate),
              })}`}
              skipLangFormat
            >
              <Button
                className={withPageRedesign ? classes.buttonRedesign : ''}
                color="primary"
                variant="contained"
                fullWidth
              >
                {T('Show alternatives')}
              </Button>
            </Link>
          </Track>

          <div className={classes.spacer} />
        </React.Fragment>
      )}

      {!isUnlisted && !isOwner && (
        <div data-test-locator="Contact Advertiser Wrapper">
          <div className={classes.spacer} />

          <ListingContactAdvertiserButton
            className={withPageRedesign ? classes.buttonRedesign : ''}
          />
        </div>
      )}

      {!withPageRedesign && !isCharitable && (
        <div className={classes.faqSection}>
          <AnchorLink to="faq">
            <span className={classes.faqLink}>{T('ldp.faq.anchor')}</span>
          </AnchorLink>
        </div>
      )}

      {hasDesiredDates && (
        <React.Fragment>
          <div className={classes.spacer} />

          {!(isCharitable && isFreeListing) && (
            <React.Fragment>
              <React.Fragment>
                {!withPageRedesign && (
                  <Typography.ParagraphTitle textTransform="uppercase">
                    {T('Pay to book')}
                  </Typography.ParagraphTitle>
                )}

                <div className={classes.spacer} />
              </React.Fragment>

              <Grid container direction="row">
                <Grid item xs>
                  <Typography.Paragraph>
                    <span>{T("First month's rent")}</span>{' '}
                    <TextTooltip
                      colorType={withPageRedesign ? 'inherit' : 'disabled'}
                      tooltip={monthRentPopoverText}
                    />
                  </Typography.Paragraph>
                </Grid>
                <Grid item xs="auto">
                  <Typography.Paragraph data-test-locator="Landlord Card First Month Rent">
                    {formatCurrency(
                      pricingStructure === PricingStructure.instalment
                        ? rentalConditions.firstInstalment
                        : price,
                      currency,
                      currencyFormat,
                    )}
                  </Typography.Paragraph>
                </Grid>
              </Grid>
            </React.Fragment>
          )}

          {!(isCharitable && isFreeListing) && feeMode !== FeeMode.nofee && (
            <React.Fragment>
              <div className={classes.spacer} />

              <Grid container direction="row">
                <Grid item xs>
                  <Typography.Paragraph>
                    <span>{T('One time service fee')}</span>{' '}
                    <TextTooltip
                      colorType={withPageRedesign ? 'inherit' : 'disabled'}
                      tooltip={serviceFeePopoverText}
                    />
                  </Typography.Paragraph>
                </Grid>
                <Grid item xs="auto">
                  <span data-test-locator="Landlord Card Service Fee">
                    <Typography.Paragraph
                      component="span"
                      display="inline"
                      className={cx(
                        feeMode === FeeMode.promotional &&
                          classes.haFeeFreeAmount,
                      )}
                    >
                      {/* Shows fee in regular and nofee mode */}
                      {feeMode === FeeMode.promotional ? fee : baseFee}
                    </Typography.Paragraph>
                    {feeMode === FeeMode.promotional && (
                      <Typography.Paragraph
                        component="span"
                        display="inline"
                        color="mint"
                        fontWeight="bold"
                        className={classes.haFeeFreeLabel}
                        data-test-locator="Listing/ListingInstantBookingBlock/Free Service Fee"
                      >
                        {T('Free')}
                      </Typography.Paragraph>
                    )}
                  </span>
                </Grid>
              </Grid>
            </React.Fragment>
          )}

          {promotionalBookingFee === 'on' && <PromotionalBooking />}

          {!withPageRedesign && <hr className={classes.hr} />}

          {withPageRedesign && <Divider className={classes.divider} />}

          <Grid container direction="row">
            <Grid item xs>
              <Typography.Paragraph fontWeight="bold">
                {withPageRedesign ? T('To book') : T('Total')}
              </Typography.Paragraph>
            </Grid>
            <Grid item xs="auto">
              {isCharitable && isFreeListing ? (
                <Typography.Paragraph
                  fontWeight="bold"
                  data-test-locator="Landlord Card Total Fees"
                >
                  {T('Free')}
                </Typography.Paragraph>
              ) : (
                <Typography.Paragraph
                  fontWeight="bold"
                  data-test-locator="Landlord Card Total Fees"
                >
                  {pricingStructure === PricingStructure.instalment
                    ? formatCurrency(
                        feeMode === FeeMode.promotional
                          ? rentalConditions.firstInstalment
                          : rentalConditions.tenantFee +
                              rentalConditions.firstInstalment,
                        currency,
                        currencyFormat,
                      )
                    : formatCurrency(
                        feeMode === FeeMode.promotional ? price : total,
                        currency,
                        currencyFormat,
                      )}
                </Typography.Paragraph>
              )}
            </Grid>
          </Grid>

          {withPageRedesign &&
            pricingStructure === PricingStructure.instalment &&
            hasDesiredDates && (
              <div className={classes.paymentDialogButtonRedesign}>
                <PaymentOverviewButton
                  text={T('StructuredCosts.LDP.cta_fullpaymentoverview')}
                  startDate={rentalConditions.startDate}
                  type="secondaryDark"
                />
              </div>
            )}

          {!(isCharitable && isFreeListing) &&
            !isUnlisted &&
            !isOwner &&
            hasDesiredDates && (
              <React.Fragment>
                <div className={classes.spacer} />
                <Grid container direction="row" justifyContent="center">
                  <Typography.HelperText align="center">
                    {T('You will not be charged at this point.')}
                  </Typography.HelperText>
                </Grid>
              </React.Fragment>
            )}
        </React.Fragment>
      )}
    </Section.Text>
  );
};

export { ListingInstantBookingBlock };
